import { ChangeDetectorRef } from '@angular/core';

/**
 * Events die aantonen dat de gebruiker zélf scrolt. Een `pointerdown` telt
 * enkel als hij op de container zelf valt (= een greep in de scrollbalk), niet
 * op content of op de sticky tab-balk (anders zou een klik op de dock-tab de
 * late auto-scroll uitschakelen).
 */
const USER_SCROLL_INTENT_EVENTS = ['wheel', 'touchmove', 'keydown', 'pointerdown'] as const;

/**
 * Herbruikbare auto-scroll-helper voor paneel-content met detailsecties
 * (angsten-, voices-, memory-, love-paneel): lijnt de dichtstbijzijnde
 * scroll-container uit op een doelsectie en houdt dat gedurende een kort
 * venster (1,5 s) vast — zowel bij late layout-verschuivingen (afbeeldingen,
 * fonts, paneel-maat) als bij scrolls die niet van de gebruiker komen (de dock
 * zet bij tab-activatie zijn bewaarde positie terug, meteen én een frame
 * later). Scrolt de gebruiker zelf (wheel/touch/toets/scrollbalk), dan blijven
 * we er vanaf.
 *
 * Gebruik: construeer met het host-element, wapen via {@link arm} (bv. in een
 * effect op de paneel-data), roep {@link onViewChecked} aan vanuit
 * `ngAfterViewChecked`, {@link notifyLayoutShift} bij late layout-prikkels
 * (bv. `img (load)`) en {@link dispose} bij destroy. Voor kliknavigatie op al
 * stabiele content is er {@link jump} (één vloeiende sprong).
 */
export class HdPanelSectionScroller {
  /** Selector van de sectie waar we (zodra de content er staat) naartoe scrollen. */
  private pendingSelector: string | null = null;
  /** Wacht-conditie: pas uitlijnen zodra de sectie-content geladen is. */
  private contentReady: () => boolean = () => true;
  /** Zolang dit venster loopt, de uitlijning vasthouden. */
  private deadline = 0;
  /** De gebruiker scrolde tijdens het venster zelf: vanaf dan niet meer tegenwerken. */
  private userTookOver = false;
  /** Container waarop we tijdens het venster scroll- en input-events volgen. */
  private watchedContainer: HTMLElement | null = null;
  private readonly recheckTimeouts: ReturnType<typeof setTimeout>[] = [];
  /** Poll-tick zolang de doelsectie verborgen is (inactieve dock-tab). */
  private hiddenRecheckTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly host: HTMLElement,
    private readonly cdr: ChangeDetectorRef,
    /**
     * Zichtbare lucht boven de sectie, ná aftrek van sticky headers (zie
     * {@link stickyHeaderOffset}); de titel staat dus altijd exact zoveel px
     * onder de tab-balk of de container-rand.
     */
    private readonly topMargin = 10,
  ) {}

  /**
   * Wapent de auto-scroll naar de sectie met deze selector. Het scrollen zelf
   * gebeurt in {@link onViewChecked} zodra de sectie gerenderd is en
   * `contentReady` waar oplevert.
   */
  arm(selector: string, contentReady: () => boolean = () => true): void {
    this.pendingSelector = selector;
    this.contentReady = contentReady;
    this.deadline = Date.now() + 1500;
    this.userTookOver = false;
    this.scheduleRechecks();
  }

  /** Vanuit `ngAfterViewChecked` van het paneel-component aanroepen. */
  onViewChecked(): void {
    const selector = this.pendingSelector;
    if (selector == null) {
      return;
    }
    const section = this.host.querySelector(selector);
    // Verborgen (bv. een nog niet actieve dock-tab: panes blijven gemount):
    // metingen zijn dan 0 — wachten tot de sectie zichtbaar is. BELANGRIJK:
    // deze check staat vóór de deadline-check: het venster mag niet verlopen
    // zolang de gebruiker de tab nog niet gezien heeft. OnPush krijgt geen
    // view-check wanneer enkel de [hidden]-toggle van de dock-pane wisselt,
    // dus we schuiven de deadline op én blijven zelf periodiek een check
    // uitlokken. Zo scrolt het paneel alsnog naar de focus-poort zodra de
    // tab (auto of handmatig, hoe laat ook) zichtbaar wordt
    // (bugfix 2026-09-22, "voice-klik vanuit transit scrolt niet").
    if (section instanceof HTMLElement && section.offsetParent == null) {
      this.deadline = Date.now() + 1500;
      this.scheduleHiddenRecheck();
      return;
    }
    if (Date.now() > this.deadline) {
      this.disarm();
      return;
    }
    if (!(section instanceof HTMLElement)) {
      return;
    }
    // Wacht tot de content geladen is; anders scrollt het paneel naar een
    // positie die daarna nog verschuift.
    if (!this.contentReady()) {
      return;
    }
    this.alignToSection(section);
  }

  /** Late afbeelding-load kan de layout verschuiven; forceer een extra view-check. */
  notifyLayoutShift(): void {
    this.cdr.markForCheck();
  }

  /** Klik op een blokje: alles staat al stabiel, dus één vloeiende sprong. */
  jump(selector: string): void {
    // Expliciete keuze van de gebruiker: een nog lopende auto-scroll mag deze
    // sprong niet terugtrekken naar de vorige focus-poort.
    this.disarm();
    const section = this.host.querySelector(selector);
    if (!(section instanceof HTMLElement)) {
      return;
    }
    const container = this.findScrollContainer(section);
    if (container == null) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    container.scrollTo({ top: this.desiredScrollTop(section, container), behavior: 'smooth' });
  }

  dispose(): void {
    this.recheckTimeouts.forEach(clearTimeout);
    if (this.hiddenRecheckTimeout != null) {
      clearTimeout(this.hiddenRecheckTimeout);
      this.hiddenRecheckTimeout = null;
    }
    this.unwatchContainer();
  }

  private disarm(): void {
    this.pendingSelector = null;
    this.unwatchContainer();
  }

  /** Plant (maximaal één tegelijk) een extra view-check zolang de sectie verborgen is. */
  private scheduleHiddenRecheck(): void {
    if (this.hiddenRecheckTimeout != null) {
      return;
    }
    this.hiddenRecheckTimeout = setTimeout(() => {
      this.hiddenRecheckTimeout = null;
      this.cdr.markForCheck();
    }, 200);
  }

  /**
   * OnPush krijgt zonder prikkels geen view-checks meer; plan er kort na het
   * wapenen een paar zodat de uitlijning ook gecontroleerd wordt wanneer enkel
   * de layout (en niet de data) nog verandert. De laatste valt net ná het
   * venster en ruimt de listeners netjes op.
   */
  private scheduleRechecks(): void {
    for (const delay of [150, 400, 900, 1600]) {
      this.recheckTimeouts.push(setTimeout(() => this.cdr.markForCheck(), delay));
    }
  }

  /**
   * Lijnt de scroll-container uit zodat de sectie bovenaan staat. Direct (niet
   * smooth): een animatie zou met late layout-verschuivingen kunnen racen.
   *
   * We vergelijken met de WERKELIJKE scrollpositie, niet met de vorige
   * doelpositie: de dock zet bij het activeren van een tab zijn bewaarde
   * positie terug (meteen én nog eens een animatieframe later) en maakte zo
   * een geslaagde uitlijning stilletjes ongedaan. Panelen met een hero-
   * afbeelding werden daar toevallig van gered (de late `img (load)`
   * verschoof de layout → her-uitlijning); het love-paneel zonder afbeelding
   * bleef bovenaan staan (bugfix 2026-09-25).
   */
  private alignToSection(section: HTMLElement): void {
    const container = this.findScrollContainer(section);
    if (container == null) {
      // Geen scrollbare container (bv. spec-DOM): één keer best effort.
      this.disarm();
      section.scrollIntoView({ block: 'start' });
      return;
    }
    this.watchContainer(container);
    if (this.userTookOver) {
      this.disarm();
      return;
    }
    // Een sectie vlak bij het einde kan nooit helemaal bovenaan: klem op de
    // maximale scrollpositie, anders "corrigeren" we eeuwig een onhaalbaar doel.
    const maxTop = Math.max(0, container.scrollHeight - container.clientHeight);
    const target = Math.min(this.desiredScrollTop(section, container), maxTop);
    if (Math.abs(container.scrollTop - target) < 3) {
      return;
    }
    container.scrollTop = target;
  }

  private watchContainer(container: HTMLElement): void {
    if (this.watchedContainer === container) {
      return;
    }
    this.unwatchContainer();
    this.watchedContainer = container;
    container.addEventListener('scroll', this.onContainerScroll, { passive: true });
    for (const type of USER_SCROLL_INTENT_EVENTS) {
      container.addEventListener(type, this.onUserScrollIntent, { passive: true });
    }
  }

  private unwatchContainer(): void {
    const container = this.watchedContainer;
    if (container == null) {
      return;
    }
    container.removeEventListener('scroll', this.onContainerScroll);
    for (const type of USER_SCROLL_INTENT_EVENTS) {
      container.removeEventListener(type, this.onUserScrollIntent);
    }
    this.watchedContainer = null;
  }

  /**
   * Een scroll tijdens het venster die niet van de gebruiker kwam (de dock-
   * restore) meteen corrigeren, zonder op de volgende view-check te wachten:
   * zo zie je hooguit één frame de verkeerde positie.
   */
  private readonly onContainerScroll = (): void => {
    if (this.pendingSelector == null || this.userTookOver || Date.now() > this.deadline) {
      this.disarm();
      return;
    }
    this.onViewChecked();
  };

  private readonly onUserScrollIntent = (event: Event): void => {
    if (event.type === 'pointerdown' && event.target !== this.watchedContainer) {
      return;
    }
    this.userTookOver = true;
  };

  private desiredScrollTop(section: HTMLElement, container: HTMLElement): number {
    const offset =
      section.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
    return Math.max(0, offset - this.stickyHeaderOffset(section, container) - this.topMargin);
  }

  /**
   * Hoogte van sticky elementen (zoals de dock-tab-balk) die de bovenkant van
   * de scroll-container bedekken: de doelsectie moet dáár onder uitkomen. De
   * balk wrapt bij veel open tabs naar meerdere rijen, dus de hoogte wordt
   * live gemeten in plaats van gegokt. We lopen het pad sectie → container af
   * en meten sticky siblings die vóór dat pad staan; sticky content ónder de
   * sectie of buiten de container telt zo nooit mee.
   */
  private stickyHeaderOffset(section: HTMLElement, container: HTMLElement): number {
    let offset = 0;
    let node: HTMLElement = section;
    while (node.parentElement != null) {
      const parent = node.parentElement;
      for (const sibling of Array.from(parent.children)) {
        if (sibling === node) {
          break;
        }
        if (!(sibling instanceof HTMLElement)) {
          continue;
        }
        const style = getComputedStyle(sibling);
        if (style.position !== 'sticky') {
          continue;
        }
        const stickyTop = Number.parseFloat(style.top);
        offset = Math.max(
          offset,
          (Number.isFinite(stickyTop) ? Math.max(stickyTop, 0) : 0) +
            sibling.getBoundingClientRect().height,
        );
      }
      if (parent === container) {
        break;
      }
      node = parent;
    }
    return offset;
  }

  /** Dichtstbijzijnde verticaal scrollbare voorouder (de paneel-body). */
  private findScrollContainer(start: HTMLElement): HTMLElement | null {
    let node: HTMLElement | null = start.parentElement;
    while (node != null && node !== document.body) {
      const style = getComputedStyle(node);
      const scrollable = /(auto|scroll|overlay)/.test(style.overflowY);
      if (scrollable && node.scrollHeight > node.clientHeight + 1) {
        return node;
      }
      node = node.parentElement;
    }
    return null;
  }
}
